import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { CityImage } from 'pages/avia/my-ticket/components/CityImage'
import PriceChanged from 'pages/avia/purchase-form/components/PriceChanged'
import { PaymentMethod } from 'pages/avia/purchase-form/pages/confirm/components/payment-method'
import { PromoCodeAvia } from 'pages/avia/purchase-form/pages/confirm/components/promo-code-avia'
import { PaymentType } from 'pages/avia/purchase-form/pages/confirm/model'

import { AutoRefundAction } from 'features/avia-auto-refund'

// import { ButtonOrderService } from 'entities/appeal/ui/button/button-create-service'
// import { ServiceInformation } from 'entities/appeal/ui/button/service-information'
import promoCodeStore from 'entities/promo-code/model/promo-code-store'
import userStore from 'entities/user/module/user-store'

import { BuyTicketApi } from 'shared/api/BuyTicket.ts'
import { DetailsAppeal } from 'shared/api/BuyTicketModel'
import {
  CurrentPriceOfTicket,
  MyTicketItem,
  Passenger,
} from 'shared/api/MyTicketsModel.ts'
import { CreditInfo } from 'shared/api/mplus/credit-info'
import { StatusPurchase, TYPE_APPELS } from 'shared/constants'
import ErrorImage from 'shared/images/error.png'
import { openDeepLink } from 'shared/lib/deep-link-open'
import { getErrorMessage } from 'shared/lib/errorText'
import { startPaymentFromConfirmResult } from 'shared/lib/payment-start'
import {
  prepareDetailMyTicketInfo,
  prepareShowListMyTicket,
} from 'shared/lib/ticketRender'
import { getUserIsDevelopers } from 'shared/lib/userIsDeveloper'
import { Button } from 'shared/ui/button/button.tsx'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { Loader } from 'shared/ui/loader'
import { PopupContent } from 'shared/ui/poup-content'

import CopyNumberTicket from '../../components/CopyNumberTicket'

import { AppealInfoButton } from './components/appeal-info'
import { FlightDetailInfo } from './components/flight-detail-info'
import { FlightInfoOrders } from './components/flight-info-orders'
import { FlightPassengerInfo } from './components/flight-passenger-info'
import { HeaderMyTicket } from './components/header/header'
import { InsuranceBanner } from './components/insurance-banner'
import { InsuranceInfo } from './components/insurance-info'
import { ReminderInfo } from './components/reminder-info'
import styles from './styles.module.scss'

interface Props {
  ticket?: MyTicketItem
  isArchiveTicket?: boolean
  additionalService?: DetailsAppeal[]
  onTicketRefresh: () => Promise<void>
}

interface TypePayment {
  isCertificate?: boolean
  typePayment: Extract<PaymentType, 'regular' | 'mInstalment'>
}

// const onGetFullName = (key: string, passengers: Passenger[]) => {
//   const currentPassenger = passengers?.filter(
//     (passenger) => passenger.key === key,
//   )[0]
//   return `${currentPassenger?.name?.last || ''} ${
//     currentPassenger?.name?.first || ''
//   } ${currentPassenger?.name?.middle || ''}`
// }

const MyTicket = observer((props: Props) => {
  const { ticket, additionalService, onTicketRefresh } = props
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isDevUser = getUserIsDevelopers()

  const { closeDialog, openDialog } = useDialogBottomSheet()

  const { type } = useParams()

  const [loading, setLoading] = useState<boolean>(false)

  const promoInfo = promoCodeStore.getPromoCode('avia')

  const showButtonForPay = ticket?.status === StatusPurchase.BOOKED.key

  const isPaymentVisible = showButtonForPay && type === 'active'

  if (!ticket) return null

  const onShowError = (errorMessage?: string) => {
    openDialog({
      child: (
        <PopupContent
          image={ErrorImage}
          title={t('error')}
          description={errorMessage || t('please_tey_again')}
          button={t('try_again')}
          onClick={closeDialog}
        />
      ),
      isDialog: false,
    })
  }

  const onStartPayment = async (props: TypePayment) => {
    if (loading) return

    setLoading(true)

    try {
      const { data: TicketData } = await BuyTicketApi.getCurrentPriceOfTicket(
        ticket.id,
      )

      if (TicketData.data.price_changed) {
        showPopUpPriceChanged(TicketData?.data, () => onPayTicket(props))
      } else {
        void onPayTicket(props)
      }
    } catch (err) {
      const errorText = getErrorMessage(err)

      onShowError(errorText)

      setLoading(false)
    }
  }

  const onPayTicket = async ({ typePayment }: TypePayment) => {
    setLoading(true)

    switch (typePayment) {
      case 'regular':
        try {
          // if (isCertificate) {
          //   await BuyTicketApi.onBuyTicketByCertificate({
          //     service_id: ticket.id,
          //   })

          //   // Certificate covers the whole price: no payment needed
          //   if (PriceCertificate >= ticket.price) {
          //     await BuyTicketApi.onBuyTicketByConfirm({
          //       service_id: ticket.id,
          //     })

          //     navigate({
          //       pathname: RoutingPaths.avia.purchase.tickerCertificate.path(),
          //       search: `?service_id=${ticket.id}`,
          //     })

          //     return
          //   }
          // }

          if (promoInfo?.is_free_payment) {
            const { data } = await BuyTicketApi.buyTicketByCertificate({
              id: ticket.id,
              type: 'tickets',
              payment_provider: 9,
              promocode_id: promoInfo?.id,
            })

            navigate(RoutingPaths.avia.purchase.tickerCertificate.path(), {
              state: {
                payment_id: data?.payment_id,
              },
            })

            return
          } else {
            const { data } = await BuyTicketApi.buyTicketConfirm({
              id: ticket.id,
              type: 'tickets',
              promocode_id: promoInfo?.id,
            })

            await startPaymentFromConfirmResult(data)
          }

          promoCodeStore.resetPromoInfo('avia')
        } catch (err) {
          const errorText = getErrorMessage(err)

          onShowError(errorText)
        } finally {
          setLoading(false)
        }

        break
      case 'mInstalment':
        try {
          const { data: mInstallmentResponse } =
            await CreditInfo.initPayMInstalment({
              service_id: ticket.id,
              service_type: 'tickets',
            })

          if (mInstallmentResponse?.data?.redirect_url) {
            openDeepLink(mInstallmentResponse?.data?.redirect_url)
          } else {
            onShowError(t('minstallment.error_minstallment_text'))
          }
        } catch (err) {
          const errorText = getErrorMessage(err)

          onShowError(errorText)
        } finally {
          setLoading(false)
        }

        break
    }
  }

  const showPopUpPriceChanged = (
    newPriceData: CurrentPriceOfTicket,
    onContinue: () => void,
  ) => {
    setLoading(false)

    openDialog({
      child: (
        <PriceChanged
          onBack={closeDialog}
          price={String(newPriceData?.new_price || '')}
          onContinue={() => {
            closeDialog()
            onContinue()
          }}
        />
      ),
      isDialog: false,
    })
  }

  const onAppealTicket = () => {
    navigate(`appeal_ticket`)
  }

  const onAppealForm = () => {
    navigate(RoutingPaths.feedback.path())
  }

  const onDetailPassenger = (data: Passenger) => {
    navigate(`detail/${data.id}`)
  }

  const listExchangeRefundAppeals = useMemo(
    () =>
      additionalService?.filter((service) => {
        if (
          service.topic === TYPE_APPELS.EXCHANGE ||
          service.topic === TYPE_APPELS.REFUND
        ) {
          return service
        }
      })?.[-1],
    [additionalService],
  )

  const onUsePromoCode = () => {
    openDialog({
      isDialog: false,
      isClickable: false,
      child: (
        <PromoCodeAvia
          serviceId={ticket.id}
          onAppliedPromoCode={(appliedPromo) => {
            promoCodeStore.setPromoCode('avia', appliedPromo)
            closeDialog()
          }}
        />
      ),
    })
  }

  const orderID = String(ticket?.billing_number || '')

  const passengers = ticket?.flight_details?.tickets?.[0]?.passengers

  const preparedTicket = prepareShowListMyTicket(ticket)

  // const listAdditionalService = useMemo(
  //   () =>
  //     additionalService?.filter(
  //       (service) => service.topic === TYPE_APPELS.ADDITIONAL_SERVICE,
  //     ),
  //   [additionalService],
  // )

  const { detailTicketInfo, isOneWayRoute } = prepareDetailMyTicketInfo(ticket)

  const isUsePromoCodeAvailable =
    userStore.userSettings?.is_promocode_available && isOneWayRoute

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <HeaderMyTicket
          showButtonForPay={showButtonForPay}
          bookingExpire={ticket.booking_expire}
        />
      </div>

      {loading ? (
        <Loader marginTop={150} />
      ) : (
        <>
          <CityImage data={ticket?.destination_image} />
          <div className={styles.container}>
            <div className={styles.contentPage}>
              <div>
                <CopyNumberTicket
                  status={ticket.status}
                  numberTicket={orderID}
                  pnrNumber={ticket?.pnr}
                  bonusAmount={ticket?.ticket_bonus_amount}
                  price={ticket?.price_with_promo_code || ticket.price}
                />

                {listExchangeRefundAppeals && (
                  <AppealInfoButton onClick={onAppealTicket} />
                )}

                {!!preparedTicket?.restrictions?.length && (
                  <ReminderInfo restrictions={preparedTicket.restrictions} />
                )}

                <FlightDetailInfo
                  ticket={ticket}
                  isBookedTicket={showButtonForPay}
                />

                <FlightPassengerInfo
                  ticket={ticket}
                  onDetailPassenger={onDetailPassenger}
                />

                <div className={styles.info}>
                  <FlightInfoOrders
                    ticketsDirections={detailTicketInfo}
                    receiptUrl={ticket?.ticket_receipt_url}
                  />
                </div>

                <InsuranceInfo insurances={ticket.insurances} />

                <InsuranceBanner />
              </div>

              {isPaymentVisible ? (
                <div className={styles.paymentBlock}>
                  <PaymentMethod
                    isLoading={loading}
                    promoCode={promoInfo}
                    selectedTicket={ticket}
                    isMplusEnabled={false}
                    finalPrice={Number(ticket.price || '0')}
                    countPassengerInfo={passengers?.length || 0}
                    isPromoAvailable={isUsePromoCodeAvailable}
                    onUsePromoCode={onUsePromoCode}
                    onRegularPay={(isCertificate) =>
                      onStartPayment({ typePayment: 'regular', isCertificate })
                    }
                    onMInstalmentPayment={() =>
                      onStartPayment({ typePayment: 'mInstalment' })
                    }
                  />
                </div>
              ) : null}
              {/* {!showButtonForPay ? (
                listAdditionalService?.length ? (
                  <ServiceInformation
                    onShowError={onShowError}
                    appeals={listAdditionalService}
                    getFullName={(key: string) =>
                      onGetFullName(key, passengers)
                    }
                  />
                ) : (
                  <ButtonOrderService
                    billingNumber={String(ticket?.billing_number)}
                  />
                )
              ) : null} */}
              {!showButtonForPay || type === 'archive' ? (
                <div className={styles.buyTicket}>
                  <Button
                    variant="transparent"
                    onClick={onAppealForm}
                  >
                    <span className={styles.button}>{t('contact_with')}</span>
                  </Button>
                </div>
              ) : null}

              {ticket.is_auto_refund_available && isDevUser && (
                <AutoRefundAction
                  ticketId={ticket.id}
                  price={ticket.price}
                  priceWithPromoCode={ticket.price_with_promo_code}
                  onRefundSuccess={onTicketRefresh}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
})

export default MyTicket
// insurance_banner.png
